import { createAsyncThunk } from "@reduxjs/toolkit";
import type { IndividualChat } from "../types/chat.type";
import { AppDispatch, RootState } from "../store";
import type { IndividualCase } from "../types/case.type";
import {
  addCallToHistory,
  addCallbackToHistory,
  addCaseToHistory,
  addChatToHistory,
  addSpeedDialToHistory,
  addVoicemailToHistory,
  deleteCallFromHistory,
  deleteCallbackFromHistory,
  deleteCaseFromHistory,
  deleteChatFromHistory,
  deleteSpeedDialFromHistory,
  deleteVoicemailFromHistory,
  editSpeedDialToHistory,
  removeCallFromHistory,
  removeCallbackFromHistory,
  removeCaseFromHistory,
  removeChatFromHistory,
  removeSpeedDialFromHistory,
  removeVoicemailFromHistory,
  setHistory,
} from "../actions/history";
import { ChatMessage } from "../types/chat.type";
import { getInstanceAlias, persistMultiInstanceHistory } from "../utils/utils";
import { Call, ConferenceState } from "../types/call.type";
import { Callback, VoiceMail } from "../types/callback.type";
import { toast } from "react-toastify";

/**
 * Initializes the history for the agent.
 * This function sets up the initial state of the history, including conversations, callbacks, chats, and cases.
 * It is typically called when the agent logs in or the instance is switched.
 *
 * @param {string} agentId - The ID of the agent whose history is being initialized.
 * @returns {void}
 */
export const initializeHistory = createAsyncThunk<
  void,
  null,
  { dispatch: AppDispatch; state: RootState }
>("history/initializeHistory", (_ignore, thunkApi) => {
  const state: RootState = thunkApi.getState();
  const agent_id = state.agent.id as string;
  const agentData = localStorage.getItem("agent_id");
  if (agentData !== null) {
    const activeInstance = getInstanceAlias();
    const agentSavedData = JSON.parse(agentData);
    thunkApi.dispatch(
      setHistory(agentSavedData[agent_id][activeInstance] || {})
    );
  }
});

/**
 * Action to add a call to the agent's history.
 * This function dispatches an action to update the state with a new call entry.
 * It is typically called when a new call is made or received by the agent.
 *
 * @param {Call} call - The call object containing details of the call to be added to the history.
 * @returns {void}
 */
export const addCallToHistoryAction = createAsyncThunk<
  void,
  Call | ConferenceState,
  { dispatch: AppDispatch; state: RootState }
>("history/addCallToHistoryAction", (call, thunkApi) => {
  let state = thunkApi.getState();

  // Support both normal calls and conferences as voice conversations
  const hasActiveVoice =
    (state.call && state.call.id && state.call.self) ||
    (state.conference && state.conference.id && state.conference.self);
  if (!hasActiveVoice) return;

  const existingCallIndex = state.history.conversations.findIndex(
    (c: Call | ConferenceState) => c?.self?.id === call?.self?.id
  );
  if (existingCallIndex > -1) {
    thunkApi.dispatch(deleteCallFromHistory(existingCallIndex));
  }
  state = thunkApi.getState();
  if (
    state.history.conversations.length >=
    parseInt(process.env.MAX_HISTORY_ITEMS as string)
  ) {
    thunkApi.dispatch(removeCallFromHistory());
  }
  thunkApi.dispatch(addCallToHistory(call));

  //Handle multi instance history
  persistMultiInstanceHistory(thunkApi.getState());
});

export const addCallbackToHistoryAction = createAsyncThunk<
  void,
  Callback,
  { dispatch: AppDispatch; state: RootState }
>("history/addCallbackToHistoryAction", (callback, thunkApi) => {
  let state = thunkApi.getState();
  if (
    !state.callback.id ||
    !state.callback.self ||
    !state.callback.self?.callback?.callbackNumber ||
    !state.callback.customer?.address
  )
    return;

  // Remove old entry if exists
  const existingCallbackIndex = state.history.callback.findIndex(
    (c: Callback) => c.id === callback.id
  );
  if (existingCallbackIndex > -1) {
    thunkApi.dispatch(deleteCallbackFromHistory(existingCallbackIndex));
  }

  // Remove oldest if history limit reached
  state = thunkApi.getState(); // Refresh state
  const MAX_HISTORY_ITEMS =
    parseInt(process.env.MAX_HISTORY_ITEMS as string) || 50;
  if (state.history.callback.length >= MAX_HISTORY_ITEMS) {
    thunkApi.dispatch(removeCallbackFromHistory());
  }

  // Add updated callback
  thunkApi.dispatch(addCallbackToHistory(callback));

  // Persist across instances
  persistMultiInstanceHistory(thunkApi.getState());
});

export const addVoicemailToHistoryAction = createAsyncThunk<
  void,
  VoiceMail,
  { dispatch: AppDispatch; state: RootState }
>("history/addVoicemailToHistoryAction", (voicemail, thunkApi) => {
  let state = thunkApi.getState();
  if (
    !state.voicemail.id ||
    !state.voicemail.self ||
    !state.voicemail.self?.voicemail?.voicemailNumber ||
    !state.voicemail.customer?.address
  )
    return;

  const existingVoicemailIndex = state.history.voicemail.findIndex(
    (c: VoiceMail) => c.id === voicemail.id
  );
  if (existingVoicemailIndex > -1) {
    thunkApi.dispatch(deleteVoicemailFromHistory(existingVoicemailIndex));
  }
  state = thunkApi.getState();
  if (
    state.history.voicemail.length >=
    parseInt(process.env.MAX_HISTORY_ITEMS as string)
  ) {
    thunkApi.dispatch(removeVoicemailFromHistory());
  }
  thunkApi.dispatch(addVoicemailToHistory(voicemail));

  //Handle multi instance history
  persistMultiInstanceHistory(thunkApi.getState());
});

export const addChatToHistoryAction = createAsyncThunk<
  void,
  IndividualChat,
  { dispatch: AppDispatch; state: RootState }
>("history/addChatToHistoryAction", (chat, thunkApi) => {
  let state = thunkApi.getState();

  const originalChat = state.chat.chats.find(
    (c: IndividualChat) => c.id === chat.id
  );

  if (!originalChat || !chat.self?.id) return;
  const releaseChat = {
    ...originalChat,
    self: {
      ...originalChat.self,
      endTime: chat?.self?.endTime || new Date().getTime(),
      state: chat?.self?.state,
      missed: chat?.self?.missed || false,
    },
    selfState: chat?.self?.state,
  };

  const existingChatIndex = state.history.chat.findIndex(
    (c: ChatMessage) => c.id === chat.id
  );
  if (existingChatIndex > -1) {
    thunkApi.dispatch(deleteChatFromHistory(existingChatIndex));
  }

  state = thunkApi.getState();
  if (
    state.history.chat.length >=
    parseInt(process.env.MAX_CHAT_HISTORY_ITEMS as string)
  ) {
    thunkApi.dispatch(removeChatFromHistory());
  }

  thunkApi.dispatch(addChatToHistory(releaseChat));

  // For multi-instance sync
  persistMultiInstanceHistory(thunkApi.getState());
});

// Code To add case from history
export const addCaseToHistoryAction = createAsyncThunk<
  void,
  IndividualCase,
  { dispatch: AppDispatch; state: RootState }
>("history/addCaseToHistoryAction", (caseObject, thunkApi) => {
  let state = thunkApi.getState();
  if (!caseObject?.id) return;
  const liveCaseIndex = state.cases.cases.findIndex(
    (c: IndividualCase) => c.id === caseObject.id
  );
  if (liveCaseIndex === -1) return;
  const existingCaseIndex = state.history.cases.findIndex(
    (c: IndividualCase) => c.id === caseObject.id
  );
  if (existingCaseIndex > -1) {
    thunkApi.dispatch(deleteCaseFromHistory(existingCaseIndex));
  }
  state = thunkApi.getState();
  if (
    state.history.cases.length >=
    parseInt(process.env.MAX_CHAT_HISTORY_ITEMS as string)
  ) {
    thunkApi.dispatch(removeCaseFromHistory());
  }
  //check if case is already released
  thunkApi.dispatch(addCaseToHistory(caseObject));

  // For multi-instance sync
  persistMultiInstanceHistory(thunkApi.getState());
});

export const addSpeedDialToHistoryAction = createAsyncThunk<
  void,
  any,
  { dispatch: AppDispatch; state: RootState }
>("history/addSpeedDialToHistoryAction", (speedDialObject, thunkApi) => {
  let state = thunkApi.getState();
  state = thunkApi.getState();
  if (
    state.history.speedDials.length >=
    parseInt(process.env.MAX_HISTORY_ITEMS as string)
  ) {
    thunkApi.dispatch(removeSpeedDialFromHistory());
  }
  thunkApi.dispatch(addSpeedDialToHistory(speedDialObject));

  // For multi-instance sync
  persistMultiInstanceHistory(thunkApi.getState());
});

export const deleteSpeedDialFromHistoryAction = createAsyncThunk<
  void,
  any,
  { dispatch: AppDispatch; state: RootState }
>("history/deleteSpeedDialFromHistoryAction", (speedDialObject, thunkApi) => {
  thunkApi.dispatch(deleteSpeedDialFromHistory(speedDialObject));

  toast.success(`Contact removed from speed dials`);
  
  // For multi-instance sync
  persistMultiInstanceHistory(thunkApi.getState());
});

export const editSpeedDialToHistoryAction = createAsyncThunk<
  void,
  { originalPhoneNumber: string; updatedContact: any },
  { dispatch: any; state: any }
>(
  "history/editSpeedDialToHistoryAction",
  ({ originalPhoneNumber, updatedContact }, thunkApi) => {
    thunkApi.dispatch(editSpeedDialToHistory(originalPhoneNumber, updatedContact));
    persistMultiInstanceHistory(thunkApi.getState());
  }
);
